import { deleteData, getData, postData, putData } from "../api/api";
import type { PageResponse } from "../types/dashboard";
import type { SemanticQuery } from "./semanticModelService";

export type InsightReportSourceType = "OPENSEARCH" | "ADAPTOR";
export type InsightThresholdOperator = "GT" | "GTE" | "LT" | "LTE" | "EQ";

export type InsightReportJob = {
  id: string;
  name: string;
  description?: string;
  sourceType: InsightReportSourceType;
  indexName?: string;
  adaptorId?: string;
  queryLanguage?: "OPENSEARCH_DSL" | "SQL" | "MONGO";
  queryBody?: string;
  /** Warehouse adaptors: semantic query recompiled on each run (queryBody then holds the last compiled SQL). */
  semanticQuery?: SemanticQuery | null;
  scheduleCron: string;
  recipientEmails?: string[];
  metricPath?: string;
  thresholdValue?: number;
  thresholdOperator?: InsightThresholdOperator;
  enabled: boolean;
  scheduleLastRunAt?: string;
  lastError?: string;
};

export type InsightReportJobPayload = {
  name: string;
  description?: string;
  sourceType: InsightReportSourceType;
  indexName?: string;
  adaptorId?: string;
  queryLanguage?: "OPENSEARCH_DSL" | "SQL" | "MONGO";
  queryBody?: string;
  /** Warehouse adaptors: semantic query recompiled on each run (queryBody then holds the last compiled SQL). */
  semanticQuery?: SemanticQuery | null;
  scheduleCron: string;
  recipientEmails?: string[];
  metricPath?: string;
  thresholdValue?: number | null;
  thresholdOperator?: InsightThresholdOperator | null;
  enabled?: boolean;
};

export type InsightReportJobRun = {
  id: string;
  jobId: string;
  jobName?: string;
  status: string;
  startedAt?: string;
  finishedAt?: string;
  metricValue?: number;
  thresholdBreached?: boolean;
  summary?: string;
  mailSubject?: string;
  mailSent?: boolean;
  mailSkipReason?: string;
  error?: string;
  triggeredBy?: string;
};

const BASE = "/insight-report-jobs";

export const insightReportJobService = {
  list: () => getData(BASE) as Promise<InsightReportJob[]>,
  get: (id: string) => getData(`${BASE}/${id}`) as Promise<InsightReportJob>,
  create: (payload: InsightReportJobPayload) =>
    postData(BASE, payload) as Promise<InsightReportJob>,
  update: (id: string, payload: InsightReportJobPayload) =>
    putData(`${BASE}/${id}`, payload) as Promise<InsightReportJob>,
  remove: (id: string) => deleteData(`${BASE}/${id}`) as Promise<void>,
  dryRun: (id: string) => postData(`${BASE}/${id}/dry-run`, {}) as Promise<InsightReportJobRun>,
  runNow: (id: string) => postData(`${BASE}/${id}/run`, {}) as Promise<InsightReportJobRun>,
  listRuns: (id: string, page = 0, size = 20) =>
    getData(`${BASE}/${id}/runs?page=${page}&size=${size}`) as Promise<
      PageResponse<InsightReportJobRun>
    >,
};
